import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { MessageCircle, X, Send, Sparkles, CheckCircle2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const WELCOME = {
  role: "assistant",
  text: "Ahoj! Som AI asistent SuperMajstri. Poradím s cenami, termínmi a službami — stavby, rekonštrukcie, zateplenie, strechy, bazény a ďalšie. Ako vám môžem pomôcť?",
};

function getSessionId() {
  let sid = localStorage.getItem("sm_chat_sid");
  if (!sid) {
    sid = "chat-" + Math.random().toString(36).slice(2) + Date.now().toString(36);
    localStorage.setItem("sm_chat_sid", sid);
  }
  return sid;
}

export default function AIAssistant() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([WELCOME]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [contactCreated, setContactCreated] = useState(false);
  const scrollRef = useRef(null);
  const sid = useRef(getSessionId());

  // Load history when opening first time
  useEffect(() => {
    if (!open) return;
    if (messages.length > 1) return;
    axios.get(`${API}/chat/${sid.current}`)
      .then((r) => {
        const payload = r.data || {};
        // Backwards compat: API used to return array directly
        const history = Array.isArray(payload)
          ? payload
          : (payload.messages || []);
        if (history.length > 0) {
          const msgs = history.map((m) => ({ role: m.role, text: m.text }));
          // If user previously chatted but never submitted a dopyt,
          // greet them back with a tailored welcome-back message.
          if (payload.abandoned) {
            const interests = payload.interests || [];
            const intro = interests.length > 0
              ? `Vitaj späť! Minule sme sa rozprávali o ${interests.slice(0, 2).join(" a ")}. Mám vám poslať osobnú ponuku?`
              : "Vitaj späť! Pokračujeme tam kde sme skončili — mám vám poslať osobnú ponuku?";
            msgs.push({ role: "assistant", text: intro, _welcomeBack: true });
          }
          setMessages(msgs);
        }
      })
      .catch(() => {});
  }, [open, messages.length]);

  // Auto-scroll
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, busy]);

  const send = async () => {
    const msg = input.trim();
    if (!msg || busy) return;
    setMessages((m) => [...m, { role: "user", text: msg }]);
    setInput("");
    setBusy(true);
    try {
      const r = await axios.post(`${API}/chat`, {
        session_id: sid.current,
        message: msg,
      });
      setMessages((m) => [...m, { role: "assistant", text: r.data.reply }]);
      if (r.data.contact_created) {
        setContactCreated(true);
      }
    } catch (e) {
      setMessages((m) => [...m, {
        role: "assistant",
        text: "Prepáčte, momentálne sa neviem pripojiť. Zavolajte prosím 0907 64 99 64.",
      }]);
    } finally {
      setBusy(false);
    }
  };

  const onKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  return (
    <>
      {/* Floating trigger button */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          className="fixed bottom-28 right-6 lg:bottom-24 lg:right-8 z-40 group"
          data-testid="ai-assistant-open"
          aria-label="Otvoriť AI asistenta"
        >
          <span className="absolute inset-0 rounded-full bg-[#00E5FF] blur-xl opacity-40 group-hover:opacity-60 animate-pulse" />
          <span className="relative flex items-center gap-2 bg-gradient-to-br from-[#00E5FF] to-[#00b3cc] text-black font-bold px-5 py-3 rounded-full shadow-2xl shadow-[#00E5FF]/40 hover:brightness-110 transition-all">
            <Sparkles size={18} />
            <span className="hidden sm:inline">Spýtajte sa AI</span>
          </span>
        </button>
      )}

      {/* Chat panel */}
      {open && (
        <div
          className="fixed bottom-6 right-4 lg:bottom-8 lg:right-8 z-50 w-[calc(100vw-2rem)] sm:w-[400px] max-h-[80vh] flex flex-col bg-[#0f0f0f] border border-white/10 rounded-2xl shadow-2xl shadow-[#00E5FF]/20 overflow-hidden"
          data-testid="ai-assistant-panel"
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-[#00E5FF]/20 to-[#FF5E00]/10 border-b border-white/10 p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#00E5FF] to-[#00b3cc] flex items-center justify-center">
                <Sparkles size={18} className="text-black" />
              </div>
              <div>
                <div className="font-bold text-white">AI asistent</div>
                <div className="text-xs text-[#00E5FF] flex items-center gap-1">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                  SuperMajstri · online
                </div>
              </div>
            </div>
            <button
              onClick={() => setOpen(false)}
              className="text-zinc-400 hover:text-white"
              data-testid="ai-assistant-close"
              aria-label="Zavrieť AI asistenta"
            >
              <X size={20} />
            </button>
          </div>

          {/* Messages */}
          <div
            ref={scrollRef}
            className="flex-1 overflow-y-auto p-4 space-y-3"
            style={{ minHeight: "300px", maxHeight: "400px" }}
          >
            {messages.map((m, i) => (
              <div
                key={i}
                className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
              >
                <div
                  className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed whitespace-pre-wrap ${
                    m.role === "user"
                      ? "bg-[#00E5FF] text-black rounded-br-sm"
                      : "bg-white/5 text-zinc-200 border border-white/10 rounded-bl-sm"
                  }`}
                  data-testid={`ai-msg-${i}`}
                >
                  {m.text}
                </div>
              </div>
            ))}
            {busy && (
              <div className="flex justify-start">
                <div className="bg-white/5 border border-white/10 rounded-2xl px-4 py-2.5 text-zinc-400 text-sm flex gap-1">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#00E5FF] animate-bounce" style={{ animationDelay: "0ms" }} />
                  <span className="w-1.5 h-1.5 rounded-full bg-[#00E5FF] animate-bounce" style={{ animationDelay: "150ms" }} />
                  <span className="w-1.5 h-1.5 rounded-full bg-[#00E5FF] animate-bounce" style={{ animationDelay: "300ms" }} />
                </div>
              </div>
            )}
            {contactCreated && (
              <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-xl p-3 text-emerald-400 text-xs flex items-center gap-2">
                <CheckCircle2 size={14} />
                Váš dopyt bol odovzdaný. Ozveme sa do 24 hodín.
              </div>
            )}
          </div>

          {/* Input */}
          <div className="border-t border-white/10 p-3 bg-black/40">
            <div className="flex gap-2 items-end">
              <textarea
                rows={1}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={onKey}
                placeholder="Napíšte otázku..."
                className="flex-1 bg-[#0a0a0a] border border-white/10 rounded-lg px-3 py-2 text-sm resize-none max-h-24 focus:outline-none focus:border-[#00E5FF]"
                data-testid="ai-assistant-input"
                disabled={busy}
              />
              <button
                onClick={send}
                disabled={busy || !input.trim()}
                className="shrink-0 w-10 h-10 rounded-lg bg-[#00E5FF] text-black hover:brightness-110 disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center"
                data-testid="ai-assistant-send"
                aria-label="Poslať správu"
              >
                <Send size={16} />
              </button>
            </div>
            <p className="text-[10px] text-zinc-600 mt-1 text-center">
              Odpovede generuje AI · údaje sú dôverné
            </p>
          </div>
        </div>
      )}
    </>
  );
}
